import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import {
  DataTable,
  FEW_ROWS_TABLE_FRAME_CLASS,
  StackedCellText,
} from '@/components/common/DataTable';
import { HighlightText } from '@/components/common/HighlightText';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import type { MenuNameActions } from '../types/menuLabel.types';
import type { MenuLabelRow, MenuLabelUnit } from '../utils/menuLabelRows';
import { MenuLabelStatusBadge } from './MenuLabelStatusBadge';
import { MenuNameInput, ResetNameButton } from './MenuNameInput';

const STATUS_COLUMN_ID = 'status';
const ORIGINAL_NAME_SLOT_CLASS = 'w-56';
const NEW_NAME_COLUMN_CLASS = 'w-[17.5rem]';
const STATUS_SLOT_CLASS = 'flex w-[6.5rem] items-center justify-between';
const UNDO_SLOT_CLASS = 'flex size-7 items-center justify-center';

function getMenuPageColumns(
  searchText: string,
  actions: MenuNameActions,
): ColumnDef<MenuLabelRow, unknown>[] {
  return [
    {
      id: 'page',
      header: 'Original Name',
      cell: ({ row }) => (
        <div className={ORIGINAL_NAME_SLOT_CLASS}>
          <StackedCellText
            primary={<HighlightText text={row.original.shipped} highlightedText={searchText} />}
            primaryClassName="font-medium"
          />
        </div>
      ),
    },
    {
      id: 'newName',
      header: 'New Name',
      cell: ({ row }) => (
        <MenuNameInput row={row.original} actions={actions} className={NEW_NAME_COLUMN_CLASS} />
      ),
    },
    {
      id: STATUS_COLUMN_ID,
      header: 'Status',
      cell: ({ row }) => (
        <div className={STATUS_SLOT_CLASS}>
          <MenuLabelStatusBadge status={row.original.status} />
          <div className={UNDO_SLOT_CLASS}>
            <ResetNameButton row={row.original} actions={actions} />
          </div>
        </div>
      ),
    },
  ];
}

interface MenuUnitEditorProps {
  unit: MenuLabelUnit;
  searchText: string;
  actions: MenuNameActions;
}

export function MenuUnitEditor({ unit, searchText, actions }: MenuUnitEditorProps) {
  const Icon = unit.icon;
  const columns = useMemo(() => getMenuPageColumns(searchText, actions), [searchText, actions]);

  return (
    <section
      aria-label={`${unit.unit.shown} names`}
      className="flex min-h-0 min-w-0 flex-col gap-4 rounded-2xl bg-card p-5 shadow-sm ring-1 ring-primary/10"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl',
            ACCENT_MARK_CLASS,
          )}
        >
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <MenuNameInput
            row={unit.unit}
            actions={actions}
            floatingLabel="Menu name"
            className="max-w-sm min-w-48 flex-1"
          />
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="size-3.5 shrink-0" aria-hidden />
            Leave a box empty to keep the original name.
          </p>
        </div>
      </div>

      <DataTable
        data={unit.pages}
        columns={columns}
        fitContentColumnIds={[STATUS_COLUMN_ID]}
        emptyMessage="No renamed page in this menu"
        emptyHint="Show all names to see every page."
        className={FEW_ROWS_TABLE_FRAME_CLASS}
      />
    </section>
  );
}
